import { QueryClient } from 'react-query';
import axios from 'axios';
import { enqueueSnackbar, backdropSetProgress } from '../design-system';
import { authService, userVisitsService } from '../services';
import { getDeviceId } from '../utils';
import { MESSAGES } from '../const';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

type RemoveCacheProps =
  | (
      | 'container'
      | 'gate-ins'
      | 'gate-outs'
      | 'inventory'
      | 'yard-companies'
      | 'yard'
      | 'client'
      | 'employee'
      | string
    )[]
  | '*'
  | undefined;

export const removeCache = (key: RemoveCacheProps) => {
  if (key === '*' || !key) {
    return queryClient.clear();
  }

  key.forEach((queryKey) => {
    queryClient.removeQueries([queryKey]);
    queryClient.removeQueries([queryKey + '-short']);
    queryClient.removeQueries([queryKey + 's']);
  });
};

// AXIOS INTERCEPTORS
let pending = 0;
axios.defaults.baseURL = process.env.REACT_APP_TEZPORT_API_URL;
const disabledTokensForRequests = ['storage.googleapis.com'];

export const controlBackdropLoading = (value: number) => {
  pending += value;

  const backdropEl = document.querySelector('#backdrop-loading');
  const isLoading = backdropEl?.className.includes('loading');

  if (pending && !isLoading) {
    backdropSetProgress();
    backdropEl?.classList.add('loading');
  }

  if (!pending && isLoading) {
    backdropEl?.classList.remove('loading');
  }
};

let isCreatingVisit: Promise<void> | null = null;

export const backdropLoadingAutoCall = () => {
  axios.interceptors.request.use(
    async (config: any) => {
      if (!config.params || !config.params.notLoading) controlBackdropLoading(1);

      backdropSetProgress(config.url, 0);

      const withCredentials =
        disabledTokensForRequests.every((link) => config.url.indexOf(link) === -1) &&
        !config.headers.Authorization;
      config.withCredentials = withCredentials;
      if (withCredentials) {
        if (config.url !== '/user-visits' && config.method !== 'post') {
          const deviceId = getDeviceId();
          if (!deviceId) {
            if (!isCreatingVisit) {
              isCreatingVisit = userVisitsService.create().finally(() => {
                isCreatingVisit = null;
              });
            }

            await isCreatingVisit;
          }
        }

        config.headers['X-Page-Url'] =
          config.headers['X-Page-Url'] !== 'none' ? window.location.pathname : '';
      }

      if (config.params && typeof config.params.notLoading === 'boolean') {
        config.customData = { notLoading: config.params.notLoading };
        delete config.params.notLoading;
      }

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response: any) => {
      backdropSetProgress(response.config.url, 100);

      const customData = response.config.customData;
      if (!customData || !customData.notLoading) controlBackdropLoading(-1);

      return response;
    },
    (error) => {
      const customData = error.config?.customData;
      error.statusCode = error.response?.status;
      if (!customData || !customData.notLoading) controlBackdropLoading(-1);

      if (!error.response || !error.response.data || !error.response.data.message) {
        error.response = { data: { message: error.message } };
      }
      if (error.response.data.message.includes('Connection timed out')) {
        error.response.data.message = MESSAGES.ERROR_BOUNDARY.DEFAULT;
      }

      if (error.statusCode === 499) {
        alert(MESSAGES.NETWORK.WARNING.WIFI_ERROR);
        return Promise.reject(error);
      }

      if (error.statusCode === 401) {
        alert(MESSAGES.AUTH.WARNING.RETRY_LOGIN);
        authService.logoutOnError();
        return Promise.reject(error);
      }

      if (error.code === 'ERR_NETWORK') {
        if (error?.config?.url?.includes('auth/login')) {
          alert(MESSAGES.NETWORK.WARNING.TRY_AGAIN);
          authService.logoutOnError();
        } else {
          enqueueSnackbar(MESSAGES.NETWORK.ERROR, 'error');
        }
      }
      if (error.code === 'ERR_BAD_RESPONSE') {
        enqueueSnackbar(error.response.data.message, 'error');
      }

      if (
        error.statusCode === 404 &&
        error.response.data.message === MESSAGES.AUTH.ERROR.ACCOUNT_NOT_FOUND
      ) {
        authService.logoutOnError();
      }
      if (error.statusCode === 409) {
        enqueueSnackbar(MESSAGES.NETWORK.ALREADY_EXISTS, 'error');
      }

      return Promise.reject(error);
    },
  );
};
